import { computed, ref, shallowRef } from 'vue'
import { useAuthStore } from '@/stores/auth'
import {
  subscriptionService,
  parseSubscriptionCurrentPayload,
  parseSubscriptionUsagePayload,
  normalizeSubscriptionPlanType
} from '@/services/subscriptionService'

const payload = shallowRef(null)
/** Uso desde GET /subscription/usage (tiene prioridad sobre usage anidado en /current). */
const usageFromUsageEndpoint = shallowRef(null)
const loading = ref(false)

/**
 * Estado compartido de GET /subscription/current (plan, límites, uso).
 * Llamar `refresh()` tras login o al cambiar empresa activa.
 */
export function useSubscriptionCurrent() {
  const authStore = useAuthStore()

  const subscription = computed(() => payload.value?.subscription ?? null)
  const plan = computed(() => payload.value?.plan ?? null)
  // Plan efectivo (refleja el promo activo si lo hay); cae al base si no viene.
  const effectivePlan = computed(() => payload.value?.effectivePlan ?? payload.value?.plan ?? null)
  const usage = computed(() => {
    if (usageFromUsageEndpoint.value != null) return usageFromUsageEndpoint.value
    return payload.value?.usage ?? null
  })

  const planType = computed(() => normalizeSubscriptionPlanType(subscription.value?.plan_type))

  const planDisplayName = computed(() => {
    const p = plan.value
    if (p && typeof p === 'object') {
      if (p.displayName) return String(p.displayName)
      if (p.name) return String(p.name)
    }
    return ''
  })

  const subscriptionStatus = computed(() => {
    const s = subscription.value?.status
    return s != null && s !== '' ? String(s).toLowerCase() : 'active'
  })

  // Fuente de verdad para acceso según el PDF: effective_access.is_accessible
  const isAccessActive = computed(() => {
    const ea = payload.value?.effectiveAccess
    if (ea && typeof ea.is_accessible === 'boolean') return ea.is_accessible
    return payload.value?.isAccessActive ?? true
  })

  const accessMessage = computed(() => {
    return payload.value?.effectiveAccess?.access_message ?? null
  })

  // true cuando la sub del usuario fue degradada a free automáticamente y todavía no
  // se le avisó (dispara el modal de "plan vencido"). Se limpia con ackDowngrade().
  const downgradeAckPending = computed(() => payload.value?.downgradeAckPending === true)

  async function refresh() {
    if (!authStore.token) {
      payload.value = null
      usageFromUsageEndpoint.value = null
      loading.value = false
      return
    }
    loading.value = true
    try {
      const [currentData, usageData] = await Promise.all([
        subscriptionService.getCurrent(),
        subscriptionService.getUsage().catch(() => null)
      ])
      payload.value = parseSubscriptionCurrentPayload(currentData)
      const usageParsed = parseSubscriptionUsagePayload(usageData)
      usageFromUsageEndpoint.value = usageParsed
    } catch {
      payload.value = null
      usageFromUsageEndpoint.value = null
    } finally {
      loading.value = false
    }
  }

  function clear() {
    payload.value = null
    usageFromUsageEndpoint.value = null
  }

  return {
    payload,
    loading,
    subscription,
    plan,
    effectivePlan,
    usage,
    planType,
    planDisplayName,
    subscriptionStatus,
    isAccessActive,
    accessMessage,
    downgradeAckPending,
    refresh,
    clear
  }
}
